import {defineStore} from 'pinia';
import {ref} from 'vue';
import {useApiClient} from '@/hooks/api-hook.ts';
import {DatasourceResponse} from '@/models/DatasourceResponse.ts';
import {
  AgGridClientQuery,
  DashboardFile,
  DashboardFileDetails,
  DashboardHistory,
  DashboardMessage,
  DashboardMessageByFileRequest,
  DashboardMessageExtra,
  DashboardMessageRequest,
  DashboardStatus,
  DashboardVerification,
  FileStreamResult,
  PassengerDataFile
} from '@/api/client/models/index.ts';
import type { FilterModel, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import {mapFilterModelToApi, mapSortModelToApi} from '@/util/grid-to-api-mappers.ts';
import {createFileDownloadResponseHandlerOption, downloadFile} from '@/util/file-download.ts';
import type { DashboardActionTarget } from '@/Features/Dashboard/types/ActionCellRendererProps.ts';
import { getApiErrorMessages } from '@/util/error-utils.ts';

type FileActionTarget = Exclude<DashboardActionTarget, number>;

async function runFileAction<T>(
  target: FileActionTarget,
  action: (bookingNumber: string) => Promise<T>,
): Promise<{ bookingNumber: string; result: T }[]> {
  const bookingNumbers = [...new Set(target.bookingNumbers ?? (target.bookingNumber ? [target.bookingNumber] : []))];
  if (!bookingNumbers.length || bookingNumbers.some(number => !number.trim())) {
    throw new Error('Select at least one TO Number in the file details to continue.');
  }

  const results = await Promise.allSettled(bookingNumbers.map(async bookingNumber => ({
    bookingNumber,
    result: await action(bookingNumber),
  })));
  const errors = results.flatMap((result, index) => result.status === 'rejected'
    ? getApiErrorMessages(result.reason).map(message => `${bookingNumbers[index]}: ${message}`)
    : []);
  if (errors.length) throw new Error(errors.join('\n'));

  return results.flatMap(result => result.status === 'fulfilled' ? [result.value] : []);
}

function requireResponse<T>(response: T | undefined, endpoint: string): T {
  if (response === undefined) {
    throw new Error(`${endpoint} returned no response`);
  }

  return response;
}

export const useDashboardStore = defineStore('useDashboard', () => {
    // Data
    const loading = ref<boolean>(false);
    const historyLoading = ref<boolean>(false);
    const messagesLoading = ref<boolean>(false);
    const verificationsLoading = ref<boolean>(false);
    const showOnlyWithErrors = ref<boolean>(true);
    const showOnlyAssignedToMe = ref<boolean>(false);
    const showPastDepartures = ref<boolean>(false);
    const showOnlyDeparturesWithinWeek = ref<boolean>(false);

    const fieldMap: Record<string, string> = {
      fileName: 'passengerDataFile.fileName',
      uploadDate: 'created',
      tourOperator: 'passengerDataFile.tourOperator.name',
      assignedUser: 'assignedUser.firstname',
    };
    const fileFieldMap: Record<string, string> = {
      uploadDate: 'created',
      tourOperator: 'touroperator.name',
    };

    // Hooks
    const apiClient = useApiClient();

    // Functions
    async function get(request: IServerSideGetRowsRequest): Promise<DatasourceResponse<DashboardFile>> {
      loading.value = true;

      const { startRow, endRow, sortModel, filterModel } = request;

      const query: AgGridClientQuery = {
        startRow: startRow,
        endRow: endRow,
        sortModel: mapSortModelToApi(sortModel, fieldMap),
        filterModel: mapFilterModelToApi((filterModel ?? {}) as FilterModel, fieldMap)
      }

      try {
        const response = await apiClient.dashboard.getPath.post(query, {
          queryParameters: {
            showOnlyErrors: showOnlyWithErrors.value,
            showOnlyAssignedToMe: showOnlyAssignedToMe.value,
            showPastDepartures: showPastDepartures.value,
            showOnlyDeparturesWithinWeek: showOnlyDeparturesWithinWeek.value,
          }
        });

        if (!response || response.total == null || response.rows == null) {
          throw new Error('Dashboard files response was incomplete');
        }

        return {
          total: response.total,
          rows: response.rows,
        }

      }
      finally {
        loading.value = false;
      }
    }

    async function getByFile(request: IServerSideGetRowsRequest): Promise<DatasourceResponse<PassengerDataFile>> {
      loading.value = true;

      const { startRow, endRow, sortModel, filterModel } = request;

      const query: AgGridClientQuery = {
        startRow: startRow,
        endRow: endRow,
        sortModel: mapSortModelToApi(sortModel, fileFieldMap),
        filterModel: mapFilterModelToApi((filterModel ?? {}) as FilterModel, fileFieldMap)
      }

      try {
        const response = await apiClient.dashboard.getByFile.post(query, {
          queryParameters: {
            showOnlyErrors: showOnlyWithErrors.value,
            showOnlyAssignedToMe: showOnlyAssignedToMe.value,
            showPastDepartures: showPastDepartures.value,
            showOnlyDeparturesWithinWeek: showOnlyDeparturesWithinWeek.value,
          }
        });

        if (!response || response.total == null || response.rows == null) {
          throw new Error('Dashboard files response was incomplete');
        }

        return {
          total: response.total,
          rows: response.rows,
        }

      }
      finally {
        loading.value = false;
      }
    }

  async function getDetails(passengerDataFileId: number, bookingNumber?: string): Promise<DashboardFileDetails[]> {
    return requireResponse(await apiClient.dashboard.getDetails.get({
      queryParameters: {
        passengerDataFileId,
        ...(bookingNumber === undefined ? {} : { bookingNumber }),
      }
    }), 'Dashboard details');
  }

  async function downloadDetails(passengerDataFileId: number, bookingNumber?: string): Promise<FileStreamResult | undefined> {
    const response = await apiClient.dashboard.downloadDetails.get({
      options: [createFileDownloadResponseHandlerOption()],
      queryParameters: {
        passengerDataFileId,
        ...(bookingNumber === undefined ? {} : { bookingNumber }),
      },
    }) as unknown as FileStreamResult | undefined;

    return downloadFile(response, `DashboardDetails-${bookingNumber ?? passengerDataFileId}.csv`);
  }

  async function getHistory(dashboardBookingId: number): Promise<DashboardHistory[]> {
    try {
      historyLoading.value = true;
      return requireResponse(await apiClient.dashboard.getHistory.get({
        queryParameters: {
          dashboardBookingId: dashboardBookingId
        }
      }), 'Dashboard history');
    }
    finally {
      historyLoading.value = false;
    }
  }

  async function getMessages(id: DashboardActionTarget): Promise<(DashboardMessage & { bookingNumber?: string })[]> {
    if (typeof id !== 'number') {
      const results = await runFileAction(id, async bookingNumber => requireResponse(
        await apiClient.dashboard.getMessagesByFile.get({
          queryParameters: { passengerDataFileId: id.passengerDataFileId, bookingNumber },
        }), 'Dashboard file messages'));
      return results.flatMap(({ bookingNumber, result }) => result.map(message =>
        id.bookingNumbers ? { ...message, bookingNumber } : message));
    }
    return requireResponse(await apiClient.dashboard.getMessages.get({
      queryParameters: {
        dashboardBookingId: id,
      }
    }), 'Dashboard messages');
  }

  async function getVerifications(id: DashboardActionTarget): Promise<(DashboardVerification & { bookingNumber?: string })[]> {
    try {
      verificationsLoading.value = true;
      if (typeof id !== 'number') {
        const results = await runFileAction(id, async bookingNumber => requireResponse(
          await apiClient.dashboard.getVerificationsByFile.get({
            queryParameters: { passengerDataFileId: id.passengerDataFileId, bookingNumber },
          }), 'Dashboard file verifications'));
        return results.flatMap(({ bookingNumber, result }) => result.map(verification =>
          id.bookingNumbers ? { ...verification, bookingNumber } : verification));
      }
      return requireResponse(await apiClient.dashboard.getVerifications.get({
        queryParameters: {
          dashboardBookingId: id,
        }
      }), 'Dashboard verifications');
    }
    finally {
      verificationsLoading.value = false;
    }
  }

  async function getBookingStatus(id: DashboardActionTarget): Promise<string | null> {
    if (typeof id !== 'number') {
      const results = await runFileAction(id, async bookingNumber => requireResponse(
        await apiClient.dashboard.getBookingStatusByFile.get({
          queryParameters: { passengerDataFileId: id.passengerDataFileId, bookingNumber },
        }), 'Dashboard file booking status'));
      const statuses = [...new Set(results.map(({ result }) => result))];
      return statuses.length === 1 ? statuses[0] : null;
    }
    return requireResponse(await apiClient.dashboard.getBookingStatus.get({
      queryParameters: {
        dashboardBookingId: id,
      }
    }), 'Dashboard booking status');
  }

  async function sendMessage(id: DashboardActionTarget, message: string, recipientId: number, extra: DashboardMessageExtra): Promise<void> {
    if (typeof id !== 'number') {
      await runFileAction(id, async bookingNumber => {
        const request: DashboardMessageByFileRequest = {
          passengerDataFileId: id.passengerDataFileId,
          bookingNumber,
          message,
          recipientId,
          type: extra,
        };
        await apiClient.dashboard.sendMessageByFile.post(request);
      });
      return;
    }
    const request: DashboardMessageRequest = {
      dashboardBookingId: id,
      message: message,
      recipientId: recipientId,
      type: extra
    }

    await apiClient.dashboard.sendMessage.post(request);
  }

  async function retry(
    id: DashboardActionTarget,
    bkgStatus: string,
    passengerData: DashboardFileDetails[] = [],
  ): Promise<void> {
    if (typeof id !== 'number') {
      await runFileAction(id, bookingNumber => apiClient.dashboard.retryByFile.post({
        passengerDataFileId: id.passengerDataFileId,
        bookingNumber,
        bkgStatus,
        passengerData: passengerData.filter(detail => detail.bookingNumber === bookingNumber),
      }));
      return;
    }
    await apiClient.dashboard.retry.post({
      dashboardBookingId: id,
      bkgStatus,
      passengerData,
    });
  }

  async function verify(id: DashboardActionTarget): Promise<void> {
    if (typeof id !== 'number') {
      await runFileAction(id, bookingNumber => apiClient.dashboard.verifyByFile.post({
        queryParameters: { passengerDataFileId: id.passengerDataFileId, bookingNumber },
      }));
      return;
    }
    await apiClient.dashboard.verify.post({
      queryParameters: {
        dashboardBookingId: id
      }
    });
  }

  async function updateStatus(id: number, status: DashboardStatus): Promise<void> {
    await apiClient.dashboard.updateStatus.post({
      queryParameters: {
        dashboardBookingId: id,
        status: status
      }
    });
  }

  return {
    loading,
    historyLoading,
    messagesLoading,
    verificationsLoading,
    showOnlyWithErrors,
    showOnlyAssignedToMe,
    showPastDepartures,
    showOnlyDeparturesWithinWeek,
    get,
    getByFile,
    getDetails,
    downloadDetails,
    getHistory,
    getMessages,
    getVerifications,
    getBookingStatus,
    sendMessage,
    retry,
    verify,
    updateStatus
  }
});
